import { UserPresenceDisplayStatus } from '../../../store/user/user.models';

export type AppMenuItem = {
  key: 'chat' | 'tasks' | 'todo' | 'calendar' | 'calls' | 'settings' | 'bug_report';
  label: string;
  icon: string;
};

// Shared palette for avatar gradients across the home feature.
export const HOME_AVATAR_ACCENTS = [
  '#8a79d6',
  '#a59164',
  '#6574cd',
  '#5f8f86',
  '#7c87bc',
  '#5d869b',
] as const;

// Picks a stable accent from the shared palette based on an id-like seed.
export function getAvatarAccent(seed?: string | null): string {
  if (!seed) {
    return HOME_AVATAR_ACCENTS[0];
  }

  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }

  return HOME_AVATAR_ACCENTS[hash % HOME_AVATAR_ACCENTS.length];
}

// Sidebar action metadata for any future per-chat action menus.
export type ChatAction = {
  label: string;
  icon: string;
};

// Flattened chat/channel view model consumed by the sidebar and parent home container.
export type ChatItem = {
  channelId: string;
  channelFriendlyName?: string;
  lastActivityAt?: string;
  name: string;
  isGroup?: boolean;
  avatarUrl?: string | null;
  unread?: number;
  online?: boolean;
  muted?: boolean;
  pinned?: boolean;
  participantUserId?: string;
  presenceLabel?: string | null;
  presenceStatus?: UserPresenceDisplayStatus | null;
  avatar: string;
  accent: string;
};

// Presentation model used by the conversation component after store/socket messages are mapped.
export type MessageItem = {
  id?: string;
  channelId: string;
  createdAt: string;
  sender: string;
  attributes?: Record<string, string>;
  messageType?: string;
  system?: boolean;
  deleted?: boolean;
  edited?: boolean;
  time: string;
  text: string;
  attachmentName?: string | null;
  attachmentUrl?: string | null;
  attachmentDownloadUrl?: string | null;
  attachmentContentType?: string | null;
  attachmentSize?: number | null;
  avatar: string;
  accent: string;
  own?: boolean;
  dayLabel?: string;
};

export type ChatReplyPreview = {
  messageId: string;
  sender: string;
  text: string;
  attachmentName?: string | null;
};

export type ChatMention = {
  userId: string;
  name: string;
};

export type ChatTaskReference = {
  taskId: string;
  projectId: string;
  title: string;
  taskCode?: string | null;
  label: string;
};

export type ChatComposerMessage = {
  text: string;
  attachments?: File[];
  replyTo?: ChatReplyPreview | null;
  mentions?: ChatMention[];
  taskReferences?: ChatTaskReference[];
};

export type CallLogDirection = 'incoming' | 'outgoing';
export type CallLogStatus = 'completed' | 'missed' | 'declined' | 'ringing';

export type CallLogItem = {
  id: string;
  channelId: string;
  name: string;
  initials: string;
  avatarUrl?: string | null;
  accent: string;
  direction: CallLogDirection;
  status: CallLogStatus;
  mode: 'audio' | 'video';
  when: string;
  startedAt: string;
  duration?: string;
};

export type HomeMentionNotification = {
  id: string;
  channelId: string;
  messageId: string;
  title: string;
  body: string;
  sender: string;
  createdAt: string;
  readAt?: string | null;
};
